import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  getUserDatatrackQuery,
  createUserDatatrackQuery,
  deleteUserDatatrackQuery,
  updateUserDatatrackQuery,
  getModalAccessForDatatrackQuery,
  getDatatrackByFilterQuery,
} from "./queries";
import { fixUTF8InObject } from "utils/encodingHelper";
import { useUserContext } from "api/user/UserProvider";
import {
  DATATRACK_MODELS,
  QUERY_STATUS,
  REQUIRED_ROLE_BY_DATATRACK_ACTION,
  ROLE_MATCH_MODE,
  SCOPES,
} from "constants/constants";
import { extractApiFieldErrorsFlat } from "utils/apiError";
import { canUserPerformAction } from "access-roles/general-functions";
import {
  ROLE_GET_BY_MODEL,
  ROLE_DELETE_BY_MODEL,
  ROLE_CREATE_BY_MODEL,
  ROLE_ADMIN_BY_MODEL,
  ROLE_READ_BY_MODEL,
} from "access-roles/roles";

const UserDatatrackContext = createContext({});

export const useUserDatatrackContext = () => useContext(UserDatatrackContext);

export const UserDatatrackProvider = ({ ...props }) => {
  const { apiErrorResponse, setApiErrorResponse } = useUserContext();
  const [userDatatrack, setUserDatatrack] = useState({});
  const [userDatatrackStatus, setUserDatatrackStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [onChangeManagers, setOnChangeManagers] = useState(false);
  const [filteredSatisfaction, setFilteredSatisfaction] = useState([]);
  const [filteredCorporateLife, setFilteredCorporateLife] = useState([]);
  const { userScope, userRoleList } = useUserContext();

  const getModalAccessForDatatrack = useCallback(
    async ({ id }) => {
      try {
        const result = await getModalAccessForDatatrackQuery({
          id,
          userScope,
        });

        return result;
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des droits d'accés de datatrack",
          error
        );
      }
    },
    [userScope]
  );

  const userDatatrackGetter = useCallback(
    async ({ id, model, ids, personIds }) => {
      const isReadScopeDashboardComments =
        userScope === SCOPES.READ &&
        model === DATATRACK_MODELS.DASHBOARD_COMMENTS;
      const requiredRole = isReadScopeDashboardComments
        ? ROLE_READ_BY_MODEL[model]
        : [
            ROLE_GET_BY_MODEL[model],
            ROLE_ADMIN_BY_MODEL[model],
            ROLE_READ_BY_MODEL[model],
          ].filter(Boolean);

      if (
        requiredRole &&
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: requiredRole,
          mode: isReadScopeDashboardComments
            ? ROLE_MATCH_MODE.AND
            : ROLE_MATCH_MODE.OR,
        })
      ) {
        return;
      }
      try {
        setUserDatatrack((old) => ({ ...old, [model]: [] }));
        const result = await getUserDatatrackQuery({
          id,
          model,
          userScope,
          ids,
          personIds,
        });
        return setUserDatatrack((old) => ({ ...old, [model]: result }));
      } catch (e) {
        console.error("userDatatrackGetter", e);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const userDatatrackCreate = useCallback(
    async ({ data, model }) => {
      const requiredRoles = [
        ROLE_CREATE_BY_MODEL[model],
        ROLE_ADMIN_BY_MODEL[model],
      ].filter(Boolean);

      setUserDatatrackStatus(QUERY_STATUS.LOADING);
      setApiErrorResponse(null);

      if (
        REQUIRED_ROLE_BY_DATATRACK_ACTION.CREATE.includes(model) &&
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles,
          mode: ROLE_MATCH_MODE.OR,
        })
      ) {
        return;
      }
      try {
        await createUserDatatrackQuery({
          data,
          model,
          userScope,
        });
        setUserDatatrackStatus(QUERY_STATUS.SUCCESS);
        const updatedUserDatatrack = await getUserDatatrackQuery({
          id: data?.person_id,
          model,
          userScope,
        });
        return setUserDatatrack((old) => ({
          ...old,
          [model]: updatedUserDatatrack,
        }));
      } catch (e) {
        setUserDatatrackStatus(QUERY_STATUS.ERROR);
        setApiErrorResponse(
          extractApiFieldErrorsFlat(e?.response?.data?.["bad-request"])
        );
        console.error("userDatatrack", e);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const userDatatrackDeleteById = useCallback(
    async ({ id, model }) => {
      const requiredRoles = [
        ROLE_DELETE_BY_MODEL[model],
        ROLE_ADMIN_BY_MODEL[model],
      ].filter(Boolean);

      setUserDatatrackStatus(QUERY_STATUS.LOADING);

      if (
        REQUIRED_ROLE_BY_DATATRACK_ACTION.DELETE.includes(model) &&
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles,
          mode: ROLE_MATCH_MODE.OR,
        })
      ) {
        return;
      }
      try {
        await deleteUserDatatrackQuery({ id, model, userScope });
        setUserDatatrackStatus(QUERY_STATUS.SUCCESS);
        return setUserDatatrack((old) => ({
          ...old,
          [model]: old[model]?.filter((row) => row?.id !== id),
        }));
      } catch (e) {
        setUserDatatrackStatus(QUERY_STATUS.ERROR);
        console.error("userDatatrack", e);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const userDatatrackUpdateById = useCallback(
    async ({ id, data, model }) => {
      setUserDatatrackStatus(QUERY_STATUS.LOADING);
      setApiErrorResponse(null);
      try {
        await updateUserDatatrackQuery({ id, data, model, userScope });
        setUserDatatrack((old) => ({
          ...old,
          [model]: old[model]?.map((elm) => (elm.id === data.id ? data : elm)),
        }));
        return setUserDatatrackStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setUserDatatrackStatus(QUERY_STATUS.ERROR);
        setApiErrorResponse(
          extractApiFieldErrorsFlat(e?.response?.data?.["bad-request"])
        );
        console.error("userDatatrackUpdateById", e);
        throw e;
      }
    },
    [userScope]
  );

  const getSatisfactionByFilter = useCallback(
    async ({ personId }) => {
      try {
        const result = await getDatatrackByFilterQuery({
          endpoint: "satisfaction",
          scope: userScope,
          personId,
        });
        setFilteredSatisfaction(result);
        return result;
      } catch (e) {
        console.error("getSatisfactionByFilter error:", e);
        throw e;
      }
    },
    [userScope, setFilteredSatisfaction]
  );

  const getCorporateLifeByFilter = useCallback(
    async ({ personId }) => {
      try {
        const result = await getDatatrackByFilterQuery({
          endpoint: "event",
          scope: userScope,
          personId,
        });
        const fixedResult = fixUTF8InObject(result);
        setFilteredCorporateLife(fixedResult);
        return fixedResult;
      } catch (e) {
        console.error("getCorporateLifeByFilter error:", e);
        throw e;
      }
    },
    [userScope, setFilteredCorporateLife]
  );

  const values = useMemo(
    () => ({
      userDatatrack,
      setUserDatatrack,
      onChangeManagers,
      setOnChangeManagers,
      userDatatrackGetter,
      userDatatrackCreate,
      userDatatrackDeleteById,
      userDatatrackUpdateById,
      userScope,
      getModalAccessForDatatrack,
      userDatatrackStatus,
      setUserDatatrackStatus,
      apiErrorResponse,
      setApiErrorResponse,
      filteredSatisfaction,
      getSatisfactionByFilter,
      filteredCorporateLife,
      getCorporateLifeByFilter,
    }),
    [
      userDatatrackStatus,
      userDatatrack,
      onChangeManagers,
      setUserDatatrack,
      userDatatrackGetter,
      userDatatrackCreate,
      userDatatrackDeleteById,
      userDatatrackUpdateById,
      getModalAccessForDatatrack,
      userScope,
      apiErrorResponse,
      setApiErrorResponse,
      setUserDatatrackStatus,
      filteredSatisfaction,
      getSatisfactionByFilter,
      filteredCorporateLife,
      getCorporateLifeByFilter,
    ]
  );

  return <UserDatatrackContext.Provider value={values} {...props} />;
};
